import { useLingui } from "@lingui/react";
import { Box, Checkbox, CircularProgress, FormControl, FormControlLabel, FormHelperText, InputLabel, MenuItem, Select, Slider, Stack, TextField, Typography } from "@mui/material";
import {  Suspense, useEffect, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { AlertConfigurationForm, RangeConfig, ThresholdConfig } from "../../schemas/alertConfigurationSchema";
import { AlertConfigurationV2, DeviceAlertComparisonType, DeviceAlertState, MetricUnit } from "@/__generated__/graphql";
import { getUnitsPerMetric } from "@/utils/getUnitsPerMetric";
import { AlertConfigurationPreview } from "../AlertConfigurationPreview";
import { useDeviceHistoricalData } from "@features/alertsConfigurationV2/hooks/useDeviceHistoricalData";

type UnitRange = {
  min: number;
  max: number;
};

// Mapeo de unidades a sus rangos (min, max)
const UNIT_RANGES: Partial<Record<MetricUnit, UnitRange>> = {
  // Caudal
  [MetricUnit.LiterPerSecond]: { min: 0, max: 10000 },
  [MetricUnit.CubicMeterPerHour]: { min: 0, max: 3600 },
  [MetricUnit.CubicMeterPerSecond]: { min: 0, max: 10 },

  // Nivel / Distancia
  [MetricUnit.Meter]: { min: 0, max: 50 },
  [MetricUnit.Centimeter]: { min: 0, max: 100 },
  [MetricUnit.Millimeter]: { min: 0, max: 1000 },

  [MetricUnit.Percent]: { min: 0, max: 100 },

  // Temperatura
  [MetricUnit.Celsius]: { min: -50, max: 100 },
  [MetricUnit.Fahrenheit]: { min: -58, max: 212 },

  [MetricUnit.Ph]: { min: 0, max: 14 },

  [MetricUnit.CentimeterPerSecond]: { min: 0, max: 500 },

  [MetricUnit.Ntu]: { min: 0, max: 2000 },

  // Eléctrico
  // 'V': { min: 0, max: 24 }, // Ejemplo para baterías de 12/24V
  // 'mA': { min: 0, max: 20 }, // Ejemplo lazo de corriente
};

// Valores por defecto si la unidad no está en el mapa
const DEFAULT_RANGE: UnitRange = { min: 0, max: 1000 };

interface ParametersSelectionStepProps {
  metric: AlertConfigurationV2 | null;
}

const ParametersSelectionStep = ({ metric }: ParametersSelectionStepProps) => {
  const { _ } = useLingui();
  const { i18n } = useLingui();
  const { control, getValues, setValue, formState: { errors } } = useFormContext<AlertConfigurationForm>();

  const deviceId = getValues('deviceId');
  const configurations = useWatch({ control, name: 'configurations' })
  const availableUnits = getUnitsPerMetric(metric?.metricName!, i18n);

  const [initialized, setInitialized] = useState(false);

  const currentUnit = useWatch({ control, name: 'unit' }) as MetricUnit;

  const {min, max} = UNIT_RANGES[currentUnit] || DEFAULT_RANGE  // Asegura que la unidad esté en el mapa

  const  mapConfigToFormConfig = (config: AlertConfigurationV2['configurations'][number]) : RangeConfig | ThresholdConfig => {
    if (config.__typename === 'RangeStateConfiguration') {
      const modality = config.comparisonCriteria === DeviceAlertComparisonType.InsideRange ? 'in_range' : 'out_range';

      return {
        alertType: config.state as DeviceAlertState.Critical | DeviceAlertState.Ok | DeviceAlertState.Warning,
        criteria: 'range',
        modality,
        minimum: config.minValue ?? 0,
        maximum: config.maxValue ?? 100,
      };
    } else if (config.__typename === 'ThresholdStateConfiguration') {
      const modality = config.comparisonCriteria === DeviceAlertComparisonType.Above ? 'above' : 'below';

      return {
        alertType: config.state as DeviceAlertState.Critical | DeviceAlertState.Ok | DeviceAlertState.Warning,
        criteria: 'threshold',
        modality,
        threshold: config.thresholdValue ?? 50,
      };
    }
    
    throw new Error(`Unexpected configuration type: ${config.__typename}`);
  };


  useEffect(() => {
    if (initialized) return;

    const existingConfigs = getValues('configurations');
    const existingUnit = getValues('unit');

    if (existingConfigs && existingConfigs.length > 0 && existingUnit) {
      setInitialized(true);
      return;
    }

    if (metric?.configurations && metric.configurations.length > 0) {
      const formConfigurations = metric.configurations.filter(config => config.state !== DeviceAlertState.Ok).map(mapConfigToFormConfig);
      setValue('configurations', formConfigurations);

      const existingDbUnit = metric.unit;
      if (existingDbUnit) {
        setValue('unit', existingDbUnit);
      } else if (availableUnits.length > 0) {
        const defaultUnit = typeof availableUnits[0] === 'string'
          ? availableUnits[0]
          : availableUnits[0].enumValue;
        setValue('unit', defaultUnit);
      }
    } else {
      setValue('configurations', []);

      if (availableUnits.length > 0) {
        const defaultUnit = typeof availableUnits[0] === 'string'
          ? availableUnits[0]
          : availableUnits[0].enumValue;
        setValue('unit', defaultUnit);
      }
    }
    setInitialized(true)
  }, [])

  const toggleAlertType = (type: DeviceAlertState.Critical | DeviceAlertState.Warning | DeviceAlertState.Ok, min: number, max: number) => {
    const currentConfigs = getValues('configurations') || [];
    const existingConfigIndex = currentConfigs.findIndex(config => config.alertType === type);
    
    if (existingConfigIndex >= 0) {
      const filteredConfigs = currentConfigs.filter((_, index) => index !== existingConfigIndex);
      setValue('configurations', filteredConfigs);
    } else {
      const newConfig: ThresholdConfig = {
        alertType: type,
        criteria: 'threshold',
        modality: 'above',
        threshold: (min + max) / 2,
      };

      setValue('configurations', [...currentConfigs, newConfig]);
    }
  };
  
  const getConfigIndexByType = (type: DeviceAlertState) => {
    return configurations?.findIndex(config => config.alertType === type);
  }

  const isTypeEnabled = (type: DeviceAlertState) => {
    return getConfigIndexByType(type) >= 0;
  }
  return (
    <>
    <Stack
      direction='column'
      spacing={1}
      sx={{ height: '100%'}}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 1,
          gap: 3,
          justifyContent: 'start'
        }}
      >
        <div>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Typography variant='h5'>
                Determinación de tipos y rangos de alerta
              </Typography>

              <Typography>
                Establece los rasgos correspondientes a la métrica seleccionada
              </Typography>
            </Box>
            <Box>
              <Controller
                control={control}
                name='unit'
                render={({ field, fieldState: { error } }) => (
                  <FormControl>
                    <InputLabel>Unidad</InputLabel>
                    <Select
                      {...field}
                      value={field.value || ''}
                      label='Unidad'
                      disabled={availableUnits.length === 0}
                      sx={{
                        width: 200
                      }}
                      >
                        {availableUnits.map((unit) => (
                          <MenuItem key={typeof unit === 'string' ? unit : unit.value} value={unit.enumValue}>
                            {typeof unit === 'string' ? unit : unit.label}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                )}
              />
              
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* <AlertTypeConfigurationMenu
              type={DeviceAlertState.Ok}
              enabled={isTypeEnabled(DeviceAlertState.Ok)}
              onToggle={() => toggleAlertType(DeviceAlertState.Ok, min, max)}
              configIndex={getConfigIndexByType(DeviceAlertState.Ok)}
            /> */}
            <AlertTypeConfigurationMenu 
              type={DeviceAlertState.Warning}
              enabled={isTypeEnabled(DeviceAlertState.Warning)}
              onToggle={() => toggleAlertType(DeviceAlertState.Warning, min, max)}
              configIndex={getConfigIndexByType(DeviceAlertState.Warning)}
            />
            <AlertTypeConfigurationMenu 
              type={DeviceAlertState.Critical}
              enabled={isTypeEnabled(DeviceAlertState.Critical)}
              onToggle={() => toggleAlertType(DeviceAlertState.Critical, min, max)}
              configIndex={getConfigIndexByType(DeviceAlertState.Critical)}
            />
          </Box>

          {errors.configurations && !Array.isArray(errors.configurations) && (
            <FormHelperText error sx={{ textAlign: 'center' }}>
              {errors.configurations.message}
            </FormHelperText>        
          )}
        </div>
        <Suspense fallback={<PreviewFallback />}>
          <AlertConfigurationPreviewWrapper
            deviceId={deviceId}
            metric={metric!}
          />
        </Suspense>
      </Box>
    </Stack>
    </>
  );
}


const PreviewFallback = () => {
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
        height: '200px',
        display: 'grid',
        placeItems: 'center',
      }}
    >
      <Box sx={{ textAlign: 'center' }}>
        <CircularProgress />
        <Typography>
          Cargando previsualización...
        </Typography>
      </Box>
    </Box>
  );
}

interface AlertConfigurationPreviewWrapperProps {
  deviceId: string;
  metric: AlertConfigurationV2;
}

const AlertConfigurationPreviewWrapper = ({
  deviceId,
  metric
}: AlertConfigurationPreviewWrapperProps) => {
  
  const { control } = useFormContext<AlertConfigurationForm>();
  const configurations = useWatch({ control, name: 'configurations' });

  const { historicalData, unit } = useDeviceHistoricalData({
    deviceId,
    metric: metric.metricName,
  });

  return (
    <AlertConfigurationPreview
      deviceId={deviceId}
      metric={metric.metricName}
      configurations={configurations || []}
      historicalData={historicalData}
      unit={unit}
    />
  );
}


interface AlertTypeConfigurationMenuProps {
  type: DeviceAlertState.Critical | DeviceAlertState.Warning | DeviceAlertState.Ok;
  enabled: boolean;
  onToggle: () => void;
  configIndex: number;
}

type AlertStateInfo = {
  title: string;
  description: string;
};

const ALERT_STATE_INFO: Record<DeviceAlertState, AlertStateInfo> = {
  [DeviceAlertState.Critical]: {
    title: 'Acción Inmediata',
    description: 'Configura un rango de emergencia. Recibirás alertas persistentes cada 1 hora hasta que el valor salga de la zona definida.'
  },
  [DeviceAlertState.Warning]: {
    title: 'Atención Temprana',
    description: 'Establece un rango de precaución. Recibirás una alerta cada 8 horas si la métrica requiere atención pero no es urgente.'
  },
  [DeviceAlertState.Ok]: {
    title: 'Estado Normal',
    description: 'Define el rango de operación normal. No se enviarán alertas mientras la métrica se mantenga dentro de estos valores.'
  },
  [DeviceAlertState.Info]: {
    title: 'Estado Informativo',
    description: 'Define el rango de operación normal. No se enviarán alertas mientras la métrica se mantenga dentro de estos valores.'
  }
};

const AlertTypeConfigurationMenu = ({ type, enabled, onToggle, configIndex }: AlertTypeConfigurationMenuProps) => {
  const { control, setValue, getValues, formState: { errors } } = useFormContext<AlertConfigurationForm>();
  const configurations = useWatch({ control, name: 'configurations' });
  const currentConfig = configIndex >= 0 ? configurations[configIndex] : null;
  const currentUnit = useWatch({ control, name: 'unit' }) as MetricUnit;

  const {min, max} = UNIT_RANGES[currentUnit] || DEFAULT_RANGE  // Asegura que la unidad esté en el mapa
  const configErrors = Array.isArray(errors.configurations) ? errors.configurations : null;

  const getModalityOptions = (criteria: 'range' | 'threshold') => {
    switch (criteria) {
      case 'range':
        return [
          { value: 'in_range', label: 'Dentro' },
          { value: 'out_range', label: 'Fuera' },
        ];
      case 'threshold':
        return [
          { value: 'above', label: 'Sobre' },
          { value: 'below', label: 'Bajo' },
        ];
      default:
        return [];
    }
  };

  const modalityOptions = currentConfig ? getModalityOptions(currentConfig.criteria) : [];

  const getSliderValues = () => {
    if (!currentConfig) return (min + max) / 2;

    if (currentConfig?.criteria === 'range') {
      return [
        currentConfig?.minimum ?? 0,
        currentConfig?.maximum ?? 100,
      ];
    }
    return currentConfig?.threshold ?? (min + max) / 2;
  };

  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    if (currentConfig?.criteria === 'range' && Array.isArray(newValue)) {
      setValue(`configurations.${configIndex}.minimum`, newValue[0]);
      setValue(`configurations.${configIndex}.maximum`, newValue[1]);
    } else if (currentConfig?.criteria === 'threshold' && typeof newValue === 'number') {
      setValue(`configurations.${configIndex}.threshold`, newValue);
    }
  };

  const getSliderMin = () => min;
  const getSliderMax = () => max;

  const handleCriteriaChange = (newCriteria: 'range' | 'threshold') => {
    if (configIndex < 0) return;

    if (newCriteria === 'range') {
      const newConfig: RangeConfig = {
        alertType: type,
        criteria: 'range',
        modality: 'in_range',
        minimum: 0,
        maximum: 0,
      }
      setValue(`configurations.${configIndex}`, newConfig)
    } else {
      const newConfig: ThresholdConfig = {
        alertType: type,
        criteria: 'threshold',
        modality: 'above',
        threshold: 0,
      };
      setValue(`configurations.${configIndex}`, newConfig)
    }
  };

  return (
    <Box sx= {{
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#1E262F',
      borderRadius: 2,
      p: 2,
      width: '100%'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1 }}>
        <Checkbox
          checked={enabled}
          onChange={onToggle}
          sx={{
            color: (theme) => theme.palette.primary.main
          }}
        />
        <Typography variant="h6">{ALERT_STATE_INFO[type].title}</Typography>
        {!enabled && (
          <Box width='700px'>{ALERT_STATE_INFO[type].description}</Box>
        )}
        {enabled && (
          <>
          <Controller
            name={`configurations.${configIndex}.criteria`}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormControl sx={{ width: '100px'}} size='small'>
                <InputLabel>Criterio</InputLabel>
                <Select
                  {...field}
                  disabled={!enabled}
                  label='Criterio'
                  onChange={(e) => {
                    field.onChange(e);
                    handleCriteriaChange(e.target.value as 'range' | 'threshold');
                  }}
                >
                  <MenuItem value='range'>Rango</MenuItem>
                  <MenuItem value='threshold'>Umbral</MenuItem>
                </Select>
              </FormControl>

            )}
          />

          <Controller
            name={`configurations.${configIndex}.modality`}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormControl sx={{ width: '150px'}} size='small'>
                <InputLabel>Modalidad</InputLabel>
                <Select
                  {...field}
                  id='modality-select'
                  label='Modalidad'
                  disabled={!enabled}
                >
                  {modalityOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />

          {currentConfig?.criteria === 'range' && (
            <Box sx={{ gap: 2, display: 'flex', alignItems: 'center' }}>
              <Controller
                name={`configurations.${configIndex}.minimum`}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label='Mínimo'
                    helperText={error?.message}
                    type='number'
                    size='small'
                    onChange={(e) => {
                      const value = e.target.value === '' ? null : Number(e.target.value);
                      field.onChange(value);
                    }}
                    sx={{
                      width: '120px'
                    }}
                  />
                )}
              />
              <Controller
                name={`configurations.${configIndex}.maximum`}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label='Máximo'
                    helperText={error?.message}
                    error={!!error}
                    type='number'
                    size='small'
                    onChange={(e) => {
                      const value = e.target.value === '' ? null : Number(e.target.value);
                      field.onChange(value);
                    }}
                    sx={{
                      width: '120px'
                    }}                
                  />
                )}
              />
            </Box>
          )}
          
          {currentConfig?.criteria === 'threshold' && (
            <Controller
              name={`configurations.${configIndex}.threshold`}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  error={!!error}
                  label='Umbral'
                  helperText={error?.message}
                  type='number'
                  size='small'
                  onChange={(e) => {
                    const value = e.target.value === '' ? null : Number(e.target.value);
                    field.onChange(value);
                  }}
                  sx={{ width: '120px' }}
                />
              )}
            />
          )}
          </>
        )}
      </Box>
      
      <Box sx={{ height: '30px' }}>
        {enabled && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, height: 1 }}>
          <Typography variant='body2' 
            sx={{
              minwidth: '40px',
              textAlign: 'left',
              color: enabled ? 'text.primary' : 'text.disabled'
              }}
          >
            {getSliderMin()}
          </Typography>

          <Slider
            value={enabled ? getSliderValues() : getSliderMin()}
            onChange={handleSliderChange}
            step={5}
            min={getSliderMin()}
            max={getSliderMax()}
            disabled={!enabled}
            disableSwap
            sx={{
              '& .MuiSlider-thumb': {
                display: enabled ? 'block' : 'none'
              }
            }}
          />
          
          <Typography
            variant='body2'
            sx={{
              minwidth: '40px',
              textAlign: 'right',
              color: enabled ? 'text.primary' : 'text.disabled'
            }}
          >
            {getSliderMax()}
          </Typography>
        </Box>
        )}
      </Box>    </Box>
  );
}

interface ErrorProps {
  error?: string
}

export const ErrorMessage = ({ error }: ErrorProps ) => {
  if (!error) return null;

  return (
    <Typography>{error}</Typography>
  );
}

export default ParametersSelectionStep;